import { useState, useEffect, useRef, useCallback } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";

// ─── Types ────────────────────────────────────────────────────────────────────

type Statut = "nouveau" | "ouvert" | "attente_revendeur" | "escalade_pudu" | "ferme";
type TypeDemande = "information" | "installation" | "sav";

interface ChatDetail {
  id: string; numero: string; type_demande: TypeDemande; titre: string;
  statut: Statut; robot_sn?: string; client_final_nom?: string;
  client_final_adresse?: string; probleme_constate?: string;
  technicien_jobotto_id?: string; assigne_at?: string;
  created_at: string; ferme_at?: string;
  profiles?: { id: string; first_name: string; last_name: string; avatar_url?: string };
}

interface ChatMessage {
  id: string; auteur_type: string;
  auteur_portail_user_id?: string; auteur_user_id?: string;
  contenu?: string; est_note_interne: boolean; created_at: string;
  profiles?: { id: string; first_name: string; last_name: string; avatar_url?: string };
  portail_users?: { id: string; prenom?: string; nom?: string };
  chat_attachments?: ChatAttachment[];
}

interface ChatAttachment {
  id: string; filename: string; mime_type: string; size_bytes: number;
  storage_path: string; type_media?: string; signedUrl?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<TypeDemande, string> = {
  information: "Info", installation: "Installation", sav: "SAV",
};

const TYPE_COLORS: Record<TypeDemande, string> = {
  information: "bg-blue-100 text-blue-700",
  installation: "bg-amber-100 text-amber-700",
  sav: "bg-red-100 text-red-700",
};

const STATUT_LABELS: Record<Statut, string> = {
  nouveau: "Nouveau", ouvert: "Ouvert", attente_revendeur: "En attente",
  escalade_pudu: "Escaladé Pudu", ferme: "Fermé",
};

const STATUT_COLORS: Record<Statut, string> = {
  nouveau: "bg-blue-100 text-blue-700",
  ouvert: "bg-green-100 text-green-700",
  attente_revendeur: "bg-amber-100 text-amber-700",
  escalade_pudu: "bg-orange-100 text-orange-700",
  ferme: "bg-gray-100 text-gray-600",
};

function formatTime(d: string): string {
  const dt = new Date(d);
  const today = new Date();
  const isToday = dt.toDateString() === today.toDateString();
  if (isToday) return dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + " " +
    dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function initiales(prenom?: string | null, nom?: string | null): string {
  return ((prenom?.[0] ?? "") + (nom?.[0] ?? "")).toUpperCase() || "?";
}

const ALLOWED_MIME = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/heic",
  "video/mp4", "video/quicktime", "video/webm", "video/avi",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChatDetailPage({ id }: { id: string }) {
  const { portailUser } = usePortailAuth();
  const { navigate } = usePortailRouter();

  const [chat, setChat] = useState<ChatDetail | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [lastMessageAt, setLastMessageAt] = useState<string | null>(null);

  // Compose
  const [composeText, setComposeText] = useState("");
  const [composeFiles, setComposeFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Lightbox
  const [lightboxImages, setLightboxImages] = useState<ChatAttachment[]>([]);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  // ── Fetch chat detail ──────────────────────────────────────────────────────
  useEffect(() => {
    setChatLoading(true);
    authFetch(`/api/portail/chats/${id}`)
      .then(r => r.json())
      .then(d => { setChat(d.chat || null); setChatLoading(false); })
      .catch(() => setChatLoading(false));
  }, [id]);

  // ── Fetch messages ─────────────────────────────────────────────────────────
  const fetchMessages = useCallback(async (after?: string) => {
    const params = after ? `?after=${encodeURIComponent(after)}` : "";
    const r = await authFetch(`/api/portail/chats/${id}/messages${params}`);
    const d = await r.json();
    return (d.messages || []) as ChatMessage[];
  }, [id]);

  useEffect(() => {
    setMessagesLoading(true);
    fetchMessages()
      .then(msgs => {
        setMessages(msgs);
        if (msgs.length > 0) setLastMessageAt(msgs[msgs.length - 1].created_at);
        setMessagesLoading(false);
      })
      .catch(() => setMessagesLoading(false));
  }, [id]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // ── Poll every 10s ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const newMsgs = await fetchMessages(lastMessageAt || undefined);
        if (newMsgs.length > 0) {
          setMessages(prev => {
            const existing = new Set(prev.map(m => m.id));
            const added = newMsgs.filter(m => !existing.has(m.id));
            return added.length > 0 ? [...prev, ...added] : prev;
          });
          setLastMessageAt(newMsgs[newMsgs.length - 1].created_at);
        }
      } catch {}
    }, 10_000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [id, lastMessageAt, fetchMessages]);

  // ── Send message ───────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!composeText.trim() && composeFiles.length === 0) return;
    if (chat?.statut === "ferme") return;
    setSending(true);
    setSendError(null);
    try {
      const fd = new FormData();
      fd.append("contenu", composeText);
      composeFiles.forEach(f => fd.append("attachments", f));

      const r = await authFetch(`/api/portail/chats/${id}/messages`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) { setSendError(d.message || "Erreur"); return; }

      setComposeText("");
      setComposeFiles([]);
      // Reload all messages
      const msgs = await fetchMessages();
      setMessages(msgs);
      if (msgs.length > 0) setLastMessageAt(msgs[msgs.length - 1].created_at);
    } catch { setSendError("Une erreur est survenue"); }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSend(); }
  };

  const handleFiles = (files: File[]) => {
    const valid = files.filter(f => ALLOWED_MIME.includes(f.type));
    setComposeFiles(prev => [...prev, ...valid].slice(0, 10));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  }, []);

  const isClosed = chat?.statut === "ferme";

  // ── Loading ────────────────────────────────────────────────────────────────
  if (chatLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
        <p className="text-muted-foreground">Chat introuvable ou accès refusé.</p>
        <button onClick={() => navigate("/chats")} className="mt-4 text-primary text-sm font-medium hover:underline">
          ← Retour aux chats
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-4rem)] max-h-[calc(100dvh-4rem)] bg-background">

      {/* Lightbox */}
      {lightboxImages.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxImages([])}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
            onClick={() => setLightboxImages([])}
          >
            <XIcon size={24} />
          </button>
          {lightboxImages.length > 1 && (
            <>
              <button
                className="absolute left-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3"
                onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + lightboxImages.length) % lightboxImages.length); }}
              >
                <ChevronLeftIcon size={28} />
              </button>
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3"
                onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % lightboxImages.length); }}
              >
                <ChevronRightIcon size={28} />
              </button>
            </>
          )}
          <img
            src={lightboxImages[lightboxIdx]?.signedUrl || ""}
            alt={lightboxImages[lightboxIdx]?.filename}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-4 text-white/50 text-xs">{lightboxImages[lightboxIdx]?.filename}</p>
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="shrink-0 border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            data-testid="button-back-chats"
            onClick={() => navigate("/chats")}
            className="p-1.5 -ml-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <ArrowLeftIcon size={20} />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-muted-foreground">{chat.numero}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[chat.type_demande] || "bg-secondary text-muted-foreground"}`}>
                {TYPE_LABELS[chat.type_demande] || chat.type_demande}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[chat.statut] || ""}`}>
                {STATUT_LABELS[chat.statut] || chat.statut}
              </span>
            </div>
            <p className="font-semibold text-foreground truncate mt-0.5">{chat.titre}</p>
            {chat.type_demande === "sav" && (chat.robot_sn || chat.client_final_nom) && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {chat.robot_sn && <span>SN: {chat.robot_sn.slice(0, 14)}{chat.robot_sn.length > 14 ? "…" : ""}</span>}
                {chat.robot_sn && chat.client_final_nom && <span> · </span>}
                {chat.client_final_nom && <span>{chat.client_final_nom}</span>}
              </p>
            )}
            {chat.profiles && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Technicien : {chat.profiles.first_name} {chat.profiles.last_name}
              </p>
            )}
          </div>
        </div>

        {/* Closed banner */}
        {isClosed && (
          <div className="mt-3 flex items-center justify-between gap-3 bg-gray-100 dark:bg-gray-800/60 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Ce chat est clôturé. Vous pouvez ouvrir un nouveau chat si nécessaire.
            </p>
            <button
              onClick={() => navigate("/chats")}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors"
            >
              <PlusIcon size={12} />
              Nouveau chat
            </button>
          </div>
        )}
      </div>

      {/* ── CONVERSATION ── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messagesLoading ? (
          <div className="flex justify-center py-10">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-3">
              <MessageCircleIcon size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Aucun message pour le moment.</p>
            <p className="text-xs text-muted-foreground mt-1">L'équipe Jobotto va prendre en charge votre demande.</p>
          </div>
        ) : (
          messages.map(msg => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              currentPortailUserId={portailUser?.id || ""}
              onImageClick={(imgs, idx) => { setLightboxImages(imgs); setLightboxIdx(idx); }}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ── COMPOSE ── */}
      {isClosed ? (
        <div className="shrink-0 border-t border-border bg-card px-4 py-3">
          <p className="text-sm text-center text-muted-foreground">Ce chat est fermé · Aucun envoi possible</p>
        </div>
      ) : (
        <div className="shrink-0 border-t border-border bg-card px-4 py-3">
          {/* File previews */}
          {composeFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {composeFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-secondary rounded-lg px-2 py-1 text-xs">
                  {f.type.startsWith("image/") ? <ImageIcon size={12} /> : f.type.startsWith("video/") ? <VideoIcon size={12} /> : <FileTextIcon size={12} />}
                  <span className="max-w-[100px] truncate">{f.name}</span>
                  <button onClick={() => setComposeFiles(p => p.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                    <XIcon size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {sendError && (
            <p className="text-xs text-red-500 mb-2">{sendError}</p>
          )}

          <div
            className={`border rounded-xl transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border"}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <textarea
              data-testid="textarea-compose"
              value={composeText}
              onChange={e => setComposeText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Votre message… (Cmd+Entrée pour envoyer)"
              rows={3}
              className="w-full px-3 pt-2.5 bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none"
            />
            <div className="flex items-center justify-between px-3 pb-2">
              <div className="flex items-center gap-0.5">
                <button
                  data-testid="button-attach-files"
                  onClick={() => fileRef.current?.click()}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  title="Joindre des fichiers"
                >
                  <PaperclipIcon size={16} />
                </button>
                <button
                  data-testid="button-camera"
                  onClick={() => cameraRef.current?.click()}
                  className="md:hidden p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  title="Prendre une photo"
                >
                  <CameraIcon size={16} />
                </button>
              </div>
              <button
                data-testid="button-send-message"
                onClick={handleSend}
                disabled={(!composeText.trim() && composeFiles.length === 0) || sending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-primary/90 transition-colors"
              >
                {sending
                  ? <div className="w-3.5 h-3.5 border border-white/70 border-t-transparent rounded-full animate-spin" />
                  : <SendIcon size={13} />
                }
                Envoyer
              </button>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ALLOWED_MIME.join(",")}
            className="hidden"
            onChange={e => handleFiles(Array.from(e.target.files || []))}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={e => handleFiles(Array.from(e.target.files || []))}
          />
          <p className="text-[10px] text-muted-foreground text-right mt-1">Cmd+Entrée · Max 10 fichiers</p>
        </div>
      )}
    </div>
  );
}

// ─── MessageBubble ────────────────────────────────────────────────────────────

function MessageBubble({
  msg, currentPortailUserId, onImageClick,
}: {
  msg: ChatMessage;
  currentPortailUserId: string;
  onImageClick: (images: ChatAttachment[], idx: number) => void;
}) {
  const isSysteme = msg.auteur_type === "systeme";
  const isRevendeur = msg.auteur_type === "revendeur";
  const isJobotto = msg.auteur_type === "jobotto";
  const isMe = isRevendeur && msg.auteur_portail_user_id === currentPortailUserId;

  if (isSysteme) {
    return (
      <div className="flex justify-center my-1">
        <span className="text-[11px] text-muted-foreground italic bg-secondary/70 px-3 py-1 rounded-full">
          {msg.contenu}
        </span>
      </div>
    );
  }

  const prenom = isJobotto
    ? (msg.profiles?.first_name || "Jobotto")
    : (msg.portail_users?.prenom || "Vous");
  const nom = isJobotto
    ? (msg.profiles?.last_name || "")
    : (msg.portail_users?.nom || "");
  const avatarUrl = isJobotto ? msg.profiles?.avatar_url : undefined;
  const label = isJobotto ? "Jobotto" : "Vous";

  const images = (msg.chat_attachments || []).filter(a => a.type_media === "image");
  const videos = (msg.chat_attachments || []).filter(a => a.type_media === "video");
  const docs = (msg.chat_attachments || []).filter(a => a.type_media === "document");

  return (
    <div className={`flex gap-2.5 ${isJobotto ? "flex-row" : "flex-row-reverse"}`}>
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-1 ${isJobotto ? "bg-primary/10 text-primary" : "bg-secondary text-foreground"}`}>
        {avatarUrl
          ? <img src={avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
          : initiales(prenom, nom)
        }
      </div>

      <div className={`flex flex-col gap-1 max-w-[78%] ${isJobotto ? "items-start" : "items-end"}`}>
        {/* Meta */}
        <div className={`flex items-center gap-1.5 text-[11px] text-muted-foreground ${isJobotto ? "" : "flex-row-reverse"}`}>
          <span className="font-medium text-foreground">{isJobotto ? `${prenom} ${nom}`.trim() : prenom}</span>
          <span>·</span>
          <span>{label}</span>
          <span>·</span>
          <span>{formatTime(msg.created_at)}</span>
        </div>

        {/* Bubble */}
        {(msg.contenu || images.length > 0 || videos.length > 0 || docs.length > 0) && (
          <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed break-words ${
            isJobotto
              ? "bg-card border border-border text-foreground rounded-tl-sm"
              : "bg-primary/15 text-foreground rounded-tr-sm"
          }`}>
            {msg.contenu && <p className="whitespace-pre-wrap">{msg.contenu}</p>}

            {/* Images */}
            {images.length > 0 && (
              <div className={`grid gap-1 mt-2 ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                {images.map((img, i) => (
                  <img
                    key={img.id}
                    src={img.signedUrl || ""}
                    alt={img.filename}
                    className="rounded-lg max-h-52 w-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => onImageClick(images, i)}
                  />
                ))}
              </div>
            )}

            {/* Videos */}
            {videos.map(vid => (
              <div key={vid.id} className="mt-2">
                <video src={vid.signedUrl || ""} controls className="rounded-lg max-h-52 w-full" />
              </div>
            ))}

            {/* Documents */}
            {docs.map(doc => (
              <a
                key={doc.id}
                href={doc.signedUrl || ""}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 mt-2 bg-secondary/60 rounded-lg px-3 py-2 hover:bg-secondary transition-colors text-xs"
              >
                <FileTextIcon size={14} className="text-muted-foreground shrink-0" />
                <span className="flex-1 truncate font-medium">{doc.filename}</span>
                <span className="text-muted-foreground shrink-0">{formatBytes(doc.size_bytes)}</span>
                <DownloadIcon size={13} className="text-muted-foreground shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Inline icon components (no external import needed) ───────────────────────

function ArrowLeftIcon({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>;
}
function PlusIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5v14" /></svg>;
}
function MessageCircleIcon({ size = 24, className = "" }: { size?: number; className?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
}
function PaperclipIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>;
}
function SendIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>;
}
function XIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>;
}
function ImageIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></svg>;
}
function VideoIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8Z" /><rect width="14" height="12" x="2" y="6" rx="2" ry="2" /></svg>;
}
function FileTextIcon({ size = 14, className = "" }: { size?: number; className?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></svg>;
}
function DownloadIcon({ size = 13, className = "" }: { size?: number; className?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>;
}
function CameraIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" /></svg>;
}
function ChevronLeftIcon({ size = 28 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>;
}
function ChevronRightIcon({ size = 28 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>;
}
